/**
 * useChefConversation — the conversation itself, owned ABOVE the window.
 *
 * It lives in AskChef, which stays mounted with the list, for two reasons:
 * closing the window mid-answer must not lose the answer, and reopening in the
 * same visit must come back to the same chef and the same conversation.
 *
 * Three rules are enforced here and nowhere else:
 *   1. ONE CHEF PER CONVERSATION. Switching chef starts a new conversation with
 *      that chef's own greeting; nothing of the previous chef is carried over.
 *   2. AN ANSWER THAT ARRIVES LATE IS DROPPED. Every send is stamped; if the
 *      conversation was switched or restarted while the answer was travelling,
 *      the answer belongs to a conversation that no longer exists.
 *   3. ONE GREETING. The chef's own line already opens with a hello, so when
 *      the shopper's name is used that hello is dropped.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const HELLO = /^(hi|hello|hey|salut|bonjour|allô|allo|hola|buenas)\s*[!,.¡]*\s*/i;

export default function useChefConversation({ api, config, lang }) {
    const { t } = useTranslation();
    const [persona, setPersona] = useState(null);
    const [messages, setMessages] = useState([]);
    const [chatId, setChatId] = useState(null);
    const [sending, setSending] = useState(false);
    const [left, setLeft] = useState(config?.questions?.remaining ?? null);
    const stamp = useRef(0);

    const people = config?.personas || [];
    const current = people.find((p) => p.key === persona) || null;
    const name = config?.member_name;

    const helloFrom = useCallback((p) => {
        const own = (p?.greeting || '').trim();
        if (!name || !own) return own;
        const body = own.replace(HELLO, '');
        return `${t('askChef.greetName', { name })} ${body || own}`;
    }, [name, t]);

    /** A fresh conversation with ONE chef: new chat, that chef's own greeting. */
    const startWith = useCallback((key) => {
        const p = (config?.personas || []).find((x) => x.key === key);
        stamp.current += 1;
        setPersona(key);
        setChatId(null);
        setSending(false);
        setMessages(p ? [{ role: 'chef', persona: key, text: helloFrom(p),
            source: 'greeting' }] : []);
    }, [config, helloFrom]);

    /** Same chef, clean slate. */
    const newChat = useCallback(() => {
        stamp.current += 1;
        setChatId(null);
        setSending(false);
        setMessages([]);
    }, []);

    // The greeting, once, as soon as a chef is chosen.
    useEffect(() => {
        if (!current) return;
        setMessages((m) => (m.length ? m : [{ role: 'chef', text: helloFrom(current),
            persona: current.key, source: 'greeting' }]));
    }, [current, helloFrom]);

    // A LANGUAGE SWITCH IS A FRESH CONVERSATION, SAME CHEF. Half a conversation
    // in English under a French answer is not a conversation. The reset waits
    // for the chef's greeting to arrive in the new language first.
    const langRef = useRef(lang);
    const greetRef = useRef('');
    useEffect(() => {
        const g = current?.greeting || '';
        if (!current) { langRef.current = lang; greetRef.current = g; return; }
        if (langRef.current === lang) { greetRef.current = g; return; }
        if (g && g !== greetRef.current) {
            langRef.current = lang;
            greetRef.current = g;
            startWith(current.key);
        }
    }, [lang, current, startWith]);

    const send = useCallback(async (text, { asRecipe = false } = {}) => {
        const body = (text || '').trim();
        if (!body || sending || !persona) return;
        const mine = ++stamp.current;
        const forChef = persona;
        setMessages((m) => [...m, { role: 'customer', text: body }]);
        setSending(true);
        try {
            const r = await api('/message', { method: 'POST',
                body: { persona: forChef, text: body, chat_id: chatId, lang,
                    as_recipe: asRecipe } });
            if (stamp.current !== mine) return;        // the conversation moved on
            setChatId(r.chat_id);
            // The server owns this number; null means there is no limit at all.
            setLeft(typeof r.questions_remaining === 'number' ? r.questions_remaining : null);
            setMessages((m) => [...m, {
                role: 'chef', persona: r.persona, text: r.reply, recipe: r.recipe,
                suggestions: r.suggestions || [], handoff: r.handoff, source: r.source,
                ideas: r.ideas || [], knownRecipes: r.known_recipes || [],
                extras: [], blocked: r.add_blocked_note, opinionTag: r.opinion_tag,
            }]);
        } catch (e) {
            if (stamp.current === mine) toast.error(t('askChef.failed'));
        } finally {
            if (stamp.current === mine) setSending(false);
        }
    }, [api, chatId, lang, persona, sending, t]);

    return { persona, setPersona, messages, setMessages, chatId, sending, left, setLeft,
        current, people, send, startWith, newChat, helloFrom };
}
